/**
 * Payments configuration for one tenant.
 *
 * Every field below is either an observed fact Stripe reported, a POINTER into the secret
 * store, or a boolean saying whether a credential is present. There is deliberately no field
 * for a key: the API never returns one, there is no read-back endpoint, and an operator who
 * needs the value gets it from Stripe or Infisical.
 *
 * Shaped after `domains.ts`, because the card is shaped after `DomainsCard` and for the same
 * reason — an asynchronous external system whose truth can only be observed.
 */

import { openedx } from '../../shared/lib/openedx';

export interface PaymentsEvent {
  id: number;
  event_type: string;
  message: string;
  details: Record<string, unknown>;
  created_at: string;
}

export type PaymentsState =
  | 'not_connected'
  | 'connecting'
  | 'connected'
  | 'verified'
  | 'live'
  | 'error'
  | 'released';

export interface PaymentsConfig {
  id: number;
  tenant_slug: string;
  provider: 'stripe';
  /** Public `acct_...`. Read back from Stripe at verification, never typed by an operator. */
  stripe_account_id: string;
  desired_state: 'active' | 'released';
  state: PaymentsState;

  /** Presence only. The value is never returned — see the module comment. */
  has_secret: boolean;
  has_webhook_secret: boolean;
  /** The Infisical PATH, so an operator knows where to look. Never a value. */
  secret_ref: string;
  webhook_secret_ref: string;
  /** Four characters, so two keys can be told apart during a rotation. Not a credential. */
  secret_last_four: string;
  secret_written_at: string | null;
  webhook_url: string;

  charges_enabled: boolean | null;
  payouts_enabled: boolean | null;
  details_submitted: boolean | null;
  account_country: string;
  default_currency: string;
  capabilities: Record<string, string>;
  payment_method_types: string[];
  livemode: boolean | null;
  write_probe_ok: boolean | null;
  webhook_endpoint_id: string;
  webhook_endpoint_ok: boolean | null;
  last_event_at: string | null;
  observed_at: string | null;

  markup_bps: number | null;
  markup_fixed_minor: number | null;
  markup_source: 'tenant' | 'platform_default';

  next_retry_at: string | null;
  retry_count: number;
  last_error_code: string | null;
  last_error_message: string | null;
  /**
   * The operator sentence and the suggested action for `last_error_code`, computed on the
   * BACKEND from `errors.py`. Deliberately not a TypeScript copy of that table: a code added
   * on the backend must not be able to ship without the copy that makes it actionable, and two
   * copies of the mapping would drift.
   */
  error_sentence: string;
  error_action: string;

  diagnostics: {
    contracts_measured_on: string;
    measured_against: Record<string, string>;
    openedx_release: string;
  };

  events: PaymentsEvent[];
}

/**
 * Whether this deployment can actually save a key, and if not, what is in the way.
 *
 * Every field here is an ADDRESS or a state name. There is deliberately no field a value could
 * occupy — the backend's `secret_store_health.Health` has none either, and a test asserts that
 * no response from the endpoint contains anything secret-shaped.
 *
 * The states are exhaustive on the backend (`secret_store_health._STATES`) and the card fetches
 * the sentences from `/secret-store/states` rather than carrying a copy, so a state added on the
 * backend cannot ship without the copy that makes it actionable.
 */
export type SecretStoreState =
  | 'healthy'
  | 'not_configured'
  | 'in_memory_only'
  | 'unreachable'
  | 'identity_invalid'
  | 'project_unknown'
  | 'environment_unknown'
  | 'path_missing'
  | 'read_only'
  | 'unknown';

export interface SecretStoreHealth {
  state: SecretStoreState;
  /** The transport-level fault name, when there was one. Diagnostic, not copy. */
  fault: string;
  healthy: boolean;
  /** False in every state except `healthy`. The card hides the paste form when it is false. */
  can_save_a_key: boolean;
  /** The sentence a superadmin reads, computed on the BACKEND from one table. */
  sentence: string;
  next_action: string;
  next_action_sentence: string;

  backend: string;
  /** Infisical's native environment slug — `dev`, `staging`, `prod`. Never in the path. */
  environment: string;
  project: string;
  /** The folder this academy's keys go in. A path is not a credential. */
  path: string;
  can_read: boolean | null;
  can_write: boolean | null;

  /**
   * Worst-case seconds until EVERY worker sees a newly-saved key. Zero when the deployment has
   * a shared invalidation cache. This is the honest cost of reading credentials at call time
   * instead of baking them into environment variables — which is what lets a new academy start
   * selling with no restart and no deploy — so the card shows it rather than hiding it.
   */
  convergence_seconds: number;
  shared_invalidation: boolean;
  cache_ttl_seconds: number;
  /** `closed` | `open` | `half_open`. Open means the store is being left alone to recover. */
  breaker: string;
  checked_at: string;
  detail: Record<string, unknown>;
}

export interface CredentialSubmission {
  /** `sk_...` or `rk_...`. Write-only: sent once, never returned. */
  secret_key?: string;
  /** `whsec_...`. Write-only. Rotates independently of the secret key. */
  webhook_secret?: string;
}

/**
 * The payments API is mounted at `/api/payments/v1/`, a different prefix from tenancy's
 * `/api/tenant/v1/`, because it is a different plugin app with its own entry point. Written
 * out rather than derived so a reader can see the real URL.
 */
const url = (slug: string, suffix = '') =>
  `/api/payments/v1/tenants/${encodeURIComponent(slug)}/payments${suffix}`;

export const paymentsApi = {
  get: (slug: string) => openedx.get<PaymentsConfig>(url(slug)),

  /**
   * Store the tenant's credentials and verify them in one call.
   *
   * Either field may be omitted to leave that credential alone — the secret key and the
   * webhook signing secret rotate on separate schedules and for separate reasons, so rotating
   * one must not disturb the other.
   */
  submitCredentials: (slug: string, payload: CredentialSubmission) =>
    openedx.post<PaymentsConfig>(url(slug, '/credentials'), payload),

  check: (slug: string) => openedx.post<PaymentsConfig>(url(slug, '/check'), {}),

  disconnect: (slug: string) => openedx.post<PaymentsConfig>(url(slug, '/disconnect'), {}),

  setMarkup: (slug: string, markup: { markup_bps?: number | null; markup_fixed_minor?: number | null }) =>
    openedx.patch<PaymentsConfig>(url(slug), markup),

  /**
   * Can this deployment save this academy's key right now, and if not, what is in the way?
   *
   * Tenant-scoped because the answer is: the configured prefix can exist while a newly-created
   * academy's own folder does not. Called BEFORE the paste form is offered, so a failed save is
   * never the first time anybody hears the store is broken.
   *
   * Always resolves with a body, including for every unhealthy state — the endpoint answers 200
   * throughout, because the deployment's store being down is not a failure of this request, and
   * an error status would make the card show "could not load" instead of the sentence that says
   * what to fix.
   */
  secretStoreHealth: (slug: string) =>
    openedx.get<SecretStoreHealth>(url(slug, '/secret-store')),

  /**
   * Create this academy's secret-store folder. Creates FOLDERS only — no key is written, and a
   * folder is not a secret. `path_missing` is the one unhealthy state the backend can fix by
   * itself, so the card offers the fix rather than sending an operator into Infisical.
   */
  provisionSecretStore: (slug: string) =>
    openedx.post<SecretStoreHealth>(url(slug, '/secret-store/provision'), {}),
};
